import type { ServerMetadata } from "@app/lib/actions/mcp_internal_actions/tool_definition";
import { routineNeeds } from "@app/lib/api/routines/capabilities";
import { z } from "zod";

export const ROUTINES_SERVER_NAME = "routines" as const;
export const LIST_MY_ROUTINES_TOOL_NAME = "list_my_routines";
export const LIST_ROUTINE_CATALOG_TOOL_NAME = "list_routine_catalog";
export const PREVIEW_ROUTINE_TOOL_NAME = "preview_routine";
export const INSTALL_ROUTINE_TOOL_NAME = "install_routine";
export const CREATE_CUSTOM_ROUTINE_TOOL_NAME = "create_custom_routine";
export const CONFIGURE_ROUTINE_TOOL_NAME = "configure_routine";
export const ENABLE_ROUTINE_TOOL_NAME = "enable_routine";
export const TEST_ROUTINE_TOOL_NAME = "test_routine";
export const PAUSE_ROUTINE_TOOL_NAME = "pause_routine";
export const REMOVE_ROUTINE_TOOL_NAME = "remove_routine";
export const LIST_ROUTINE_RUNS_TOOL_NAME = "list_routine_runs";
export const RETRY_ROUTINE_RUN_TOOL_NAME = "retry_routine_run";

const routineId = z
  .string()
  .describe("The installed routine's sId, as list_my_routines returned.");

export const ROUTINES_TOOLS_METADATA = [
  {
    name: LIST_MY_ROUTINES_TOOL_NAME,
    description:
      "List the routines this user has already installed, with their schedule and whether each one is running or paused. Call this first whenever someone asks for recurring work: a routine that already covers it should be changed, never installed twice.",
    schema: {},
    stake: "never_ask",
    displayLabels: {
      running: "Reading your routines",
      done: "Read your routines",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: LIST_ROUTINE_CATALOG_TOOL_NAME,
    description:
      "List the routines available to install that this user has not installed yet. Match the request against this catalog before proposing anything new.",
    schema: {},
    stake: "never_ask",
    displayLabels: {
      running: "Reading the routine catalog",
      done: "Read the routine catalog",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: PREVIEW_ROUTINE_TOOL_NAME,
    description:
      "Read one catalog entry in full: what it does, when it runs, and which of its tools this workspace has connected. Every unconnected tool comes back with the connect card to show the user, verbatim.",
    schema: {
      templateId: z
        .string()
        .describe("The catalog entry's id, copied from the catalog listing."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Previewing a routine",
      done: "Previewed a routine",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: INSTALL_ROUTINE_TOOL_NAME,
    description:
      "Install a catalog routine for this user, switched off. Nothing runs until enable_routine. It returns the routine's steps and whether it still needs setup.",
    schema: {
      templateId: z
        .string()
        .describe("The catalog entry's id, copied from the catalog listing."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Installing a routine",
      done: "Installed a routine",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: CREATE_CUSTOM_ROUTINE_TOOL_NAME,
    description:
      "Create a routine the catalog does not have, switched off: its procedure, the capabilities it needs, its schedule, and the agent whose job it is. Use only after list_routine_catalog has no match and the person agreed to what it will do, when, and who runs it. Nothing runs until enable_routine.",
    schema: {
      name: z
        .string()
        .max(60)
        .describe(
          "Short name the person will recognise, e.g. Investor Update Draft."
        ),
      description: z
        .string()
        .max(160)
        .describe("One sentence on what each run produces."),
      instructions: z
        .string()
        .describe(
          "The procedure the owner follows on every run, written to the agent in the person's voice (me, my): what to read, the steps in order, the output headings, what to say on a quiet run, and that nothing is sent without approval."
        ),
      needs: z
        .array(z.enum(routineNeeds))
        .describe(
          "Capabilities the procedure uses, never product names. read_mail also covers drafting replies in Gmail; post_message covers posting or sending."
        ),
      owner: z
        .string()
        .optional()
        .describe(
          "Who runs it, when that is not the chief of staff: a teammate or an agent already running one of the person's routines, by the sId in its mention directive or by its name. Leave it out for the chief of staff, and leave it out when hiring with newAgent."
        ),
      newAgent: z
        .object({
          name: z
            .string()
            .max(40)
            .describe("The new agent's name, a role (e.g. Finance Ops)."),
          description: z
            .string()
            .max(200)
            .describe(
              "One sentence on the agent's job, in the person's terms."
            ),
        })
        .optional()
        .describe(
          "Hire a new agent to run it, only when no existing role fits and after telling the person its name and job."
        ),
      cron: z
        .string()
        .describe(
          "Five-field cron for when it runs, in the person's timezone."
        ),
      schedule: z
        .string()
        .optional()
        .describe("The same schedule in words, e.g. every Friday at 09:00."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Creating a routine",
      done: "Created a routine",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: CONFIGURE_ROUTINE_TOOL_NAME,
    description:
      "Set a routine's behaviour knobs and choose which connected tool serves each step. Call after the person has answered; pass only what changed.",
    schema: {
      routineId: z
        .string()
        .describe("The installed routine's sId, as install_routine returned."),
      settings: z
        .record(z.union([z.boolean(), z.string()]))
        .optional()
        .describe(
          "Behaviour knobs by key, as the entry's settingsSchema names them."
        ),
      resolvedTools: z
        .record(z.string())
        .optional()
        .describe(
          "Which connected server serves each step, keyed by the step's label."
        ),
    },
    stake: "low",
    displayLabels: {
      running: "Configuring a routine",
      done: "Configured a routine",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: ENABLE_ROUTINE_TOOL_NAME,
    description:
      "Switch a routine on. This is the approval: call only after the person has seen the final shape and said yes. Fails while a required step is unconnected.",
    schema: {
      routineId: z
        .string()
        .describe("The installed routine's sId, as install_routine returned."),
    },
    stake: "high",
    displayLabels: {
      running: "Enabling a routine",
      done: "Enabled a routine",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: TEST_ROUTINE_TOOL_NAME,
    description:
      "Run the routine once now, even while paused, so the person can see a real result before enabling. For an event routine this runs against the latest matching event.",
    schema: {
      routineId: z
        .string()
        .describe("The installed routine's sId, as install_routine returned."),
    },
    stake: "low",
    displayLabels: {
      running: "Test-running a routine",
      done: "Test-ran a routine",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: PAUSE_ROUTINE_TOOL_NAME,
    description:
      "Pause a routine so it stops running until enable_routine switches it back on. Use when the user says to pause, stop or turn off a routine.",
    schema: { routineId },
    stake: "low",
    displayLabels: {
      running: "Pausing a routine",
      done: "Paused a routine",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: REMOVE_ROUTINE_TOOL_NAME,
    description:
      "Remove an installed routine for good, with its settings. Pausing is usually what the user means; remove only when they say delete or remove.",
    schema: { routineId },
    stake: "high",
    displayLabels: {
      running: "Removing a routine",
      done: "Removed a routine",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: LIST_ROUTINE_RUNS_TOOL_NAME,
    description:
      "List a routine's recent runs with each run's outcome and the conversation it produced. Use when the user asks whether a routine ran, what it did, or why it failed.",
    schema: { routineId },
    stake: "never_ask",
    displayLabels: {
      running: "Reading routine runs",
      done: "Read routine runs",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: RETRY_ROUTINE_RUN_TOOL_NAME,
    description:
      "Retry one run of an event routine against the same event. Only runs list_routine_runs marks `canRetry` can be retried.",
    schema: {
      routineId,
      conversationId: z
        .string()
        .describe("The run's `conversationId`, as list_routine_runs returned."),
    },
    stake: "low",
    displayLabels: {
      running: "Retrying a routine run",
      done: "Retried a routine run",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
] as const;

export const ROUTINES_SERVER = {
  serverInfo: {
    name: ROUTINES_SERVER_NAME,
    version: "1.0.0",
    description:
      "Read the user's routines and the routine catalog, report which of a routine's tools are connected, install one paused or write one the catalog lacks, resolve its steps, test it, enable it on approval, pause or remove it, and read its runs.",
    authorization: null,
    icon: "ActionTimeIcon",
    documentationUrl: null,
  },
  tools: ROUTINES_TOOLS_METADATA,
} as const satisfies ServerMetadata;
